import React, { useState, useEffect } from 'react';
import { Form, Input, InputNumber, DatePicker, Select, Button, Card, message, Space, Descriptions, Tag, Spin } from 'antd';
import { useQuery, useMutation } from '@apollo/client';
import { useNavigate, useParams } from 'react-router-dom';
import { getCreditProductById } from '../../graphql/queries';
import { updateCreditProduct } from '../../graphql/mutation';
import moment from 'moment';
import './CreditProducts.css';

const { Option } = Select;

const CreditProductDetail = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { id } = useParams();
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);

  const { loading: queryLoading, data, refetch } = useQuery(getCreditProductById, {
    variables: { getCreditProductByIdId: id },
    onError: (error) => {
      message.error(`Error loading credit product: ${error.message}`);
    },
  });

  const [updateProduct] = useMutation(updateCreditProduct, {
    onCompleted: (data) => {
      message.success('Credit product updated successfully!');
      setIsEditing(false);
      refetch();
    },
    onError: (error) => {
      message.error(`Error updating credit product: ${error.message}`);
      setLoading(false);
    },
  });

  useEffect(() => {
    if (data?.getCreditProductById) {
      const product = data.getCreditProductById;
      form.setFieldsValue({
        name: product.name,
        amount: product.amount,
        startDate: moment(),
        endDate: moment().add(5, 'years'),
        limit: product.limit,
        price: product.price,
        type: product.type,
      });
    }
  }, [data, form]);

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const input = {
        name: values.name,
        amount: values.amount,
        startDate: moment(),
        endDate: moment().add(5, 'years'),
        limit: values.limit,
        price: values.price,
        type: values.type,
      };

      await updateProduct({
        variables: { 
          input,
          updateCreditProductId: id
        },
      });
    } catch (error) {
      console.error('Error updating credit product:', error);
    }
  };

  const productTypes = [
    { value: 'YEARLY', label: 'Yearly' },
    { value: 'WEEKLY', label: 'Weekly' },
    { value: 'MONTHLY', label: 'Monthly' },
    { value: 'LIFETIME', label: 'Lifetime' },
    { value: 'PROMO', label: 'Promo' },
  ];

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const getStatusTag = (product) => {
    if (product.isExpired) {
      return <Tag color="red">Expired</Tag>;
    }
    if (product.isUpcoming) {
      return <Tag color="blue">Upcoming</Tag>;
    }
    if (product.isActive) {
      return <Tag color="green">Active</Tag>;
    }
    return <Tag color="default">Inactive</Tag>;
  };

  if (queryLoading) {
    return (
      <div className="credit-products-container">
        <Card>
          <div style={{ textAlign: 'center', padding: '50px' }}>
            <Spin size="large" />
          </div>
        </Card>
      </div>
    );
  }

  const product = data?.getCreditProductById;

  return (
    <div className="credit-products-container">
      <Card>
        <div className="table-header">
          <h2>Credit Product Details</h2>
          <Space>
            {!isEditing && (
              <Button type="primary" onClick={() => setIsEditing(true)}>
                Edit Product
              </Button>
            )}
            <Button onClick={() => navigate('/credit-products')}>
              Back to List
            </Button>
          </Space>
        </div>

        {isEditing ? (
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
          >
            <Form.Item
              name="name"
              label="Product Name"
              rules={[{ required: true, message: 'Please enter a product name' }]}
            >
              <Input placeholder="Enter product name" />
            </Form.Item>

            <Form.Item
              name="type"
              label="Product Type"
              rules={[{ required: true, message: 'Please select a product type' }]}
            >
              <Select placeholder="Select product type">
                {productTypes.map((type) => (
                  <Option key={type.value} value={type.value}>
                    {type.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              name="amount"
              label="Credit Amount"
              rules={[{ required: true, message: 'Please enter credit amount' }]}
            >
              <InputNumber
                min={0}
                style={{ width: '100%' }}
                placeholder="Enter credit amount"
              />
            </Form.Item>

            <Form.Item
              name="price"
              label="Price (USD)"
              rules={[{ required: true, message: 'Please enter price' }]}
            >
              <InputNumber
                min={0}
                step={0.01}
                style={{ width: '100%' }}
                placeholder="Enter price in USD"
              />
            </Form.Item>

            <Form.Item
              name="limit"
              label="Usage Limit"
              rules={[{ required: true, message: 'Please enter usage limit' }]}
            >
              <InputNumber
                min={0}
                style={{ width: '100%' }}
                placeholder="Enter usage limit (0 for unlimited)"
              />
            </Form.Item>

            <Form.Item>
              <Space>
                <Button type="primary" htmlType="submit" loading={loading}>
                  Save Changes
                </Button>
                <Button onClick={() => {
                  setIsEditing(false);
                  form.resetFields();
                }}>
                  Cancel
                </Button>
              </Space>
            </Form.Item>
          </Form>
        ) : (
          <Descriptions bordered column={2}>
            <Descriptions.Item label="Product Name">{product.name}</Descriptions.Item>
            <Descriptions.Item label="Product Type">
              <Tag color={product.type === 'PREMIUM' ? 'gold' : 'blue'}>
                {product.type}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Credit Amount">{product.amount}</Descriptions.Item>
            <Descriptions.Item label="Price">{formatCurrency(product.price)}</Descriptions.Item>
            <Descriptions.Item label="Usage Limit">{product.limit || 'Unlimited'}</Descriptions.Item>
            <Descriptions.Item label="Credit Count">{product.creditCount}</Descriptions.Item>
            <Descriptions.Item label="Start Date">{formatDate(product.startDate)}</Descriptions.Item>
            <Descriptions.Item label="End Date">{formatDate(product.endDate)}</Descriptions.Item>
            <Descriptions.Item label="Status">{getStatusTag(product)}</Descriptions.Item>
            <Descriptions.Item label="Created At">{formatDate(product.createdAt)}</Descriptions.Item>
            <Descriptions.Item label="Updated At">{formatDate(product.updatedAt)}</Descriptions.Item>
          </Descriptions>
        )}
      </Card>
    </div>
  );
};

export default CreditProductDetail; 